import moment, { Moment } from 'moment';
import { parse, stringify } from 'qs';

export function fixedZero(val: number): string {
  return val * 1 < 10 ? `0${val}` : val.toString();
}

const stringToTitleCase = (str: string): string =>
  str
    .split(' ')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');

export function truncateName(nameUppercase: string): string {
  const name = nameUppercase.toLowerCase();
  if (name.length <= 14) {
    return stringToTitleCase(name);
  }

  const fullName = name.split(' ');
  let displayName = `${fullName[0]} ${fullName[fullName.length - 1]}`;
  if (displayName.length <= 14) {
    return stringToTitleCase(displayName);
  }
  displayName = `${fullName[0].substring(0, 1)}. ${fullName[fullName.length - 1]}`;
  if (displayName.length <= 14) {
    return stringToTitleCase(displayName);
  }
  return `${fullName[0].substring(0, 1)}.${fullName[fullName.length - 1].substring(0, 1)}.`.toUpperCase();
}

export function formatTime(time: number): string {
  const hours = Math.floor(time / 3600).toString();
  let mins = Math.floor((time - parseInt(hours, 10) * 3600) / 60).toString();

  if (mins.length < 2) {
    mins = `0${mins}`;
  }

  return `${hours}:${mins}`;
}

export function unSnakeify(event = '-'): string {
  let formattedString = event.replace(new RegExp('_', 'g'), ' ');
  formattedString = formattedString
    .split(' ')
    .map((item) => item.substring(0, 1).toUpperCase() + item.substring(1))
    .join(' ');
  return formattedString;
}

export function getTimeDistance(type: string): [Moment, Moment] {
  const now = new Date();
  const oneDay = 1000 * 60 * 60 * 24;

  if (type === 'today') {
    now.setHours(0);
    now.setMinutes(0);
    now.setSeconds(0);
    return [moment(now), moment(now.getTime() + (oneDay - 1000))];
  }

  if (type === 'week') {
    let day = now.getDay();
    now.setHours(0);
    now.setMinutes(0);
    now.setSeconds(0);

    if (day === 0) {
      day = 6;
    } else {
      day -= 1;
    }

    const beginTime = now.getTime() - day * oneDay;

    return [moment(beginTime), moment(beginTime + (7 * oneDay - 1000))];
  }

  if (type === 'month') {
    const year = now.getFullYear();
    const month = now.getMonth();
    const nextDate = moment(now).add(1, 'months');
    const nextYear = nextDate.year();
    const nextMonth = nextDate.month();

    return [
      moment(`${year}-${fixedZero(month + 1)}-01 00:00:00`),
      moment(moment(`${nextYear}-${fixedZero(nextMonth + 1)}-01 00:00:00`).valueOf() - 1000),
    ];
  }

  if (type === 'year') {
    const year = now.getFullYear();
    return [moment(`${year}-01-01 00:00:00`), moment(`${year}-12-31 23:59:59`)];
  }

  const year = now.getFullYear();
  return [moment('1901-01-01 00:00:00'), moment(`${year}-12-31 23:59:59`)];
}

export function getPageQuery(): string {
  return parse(window.location.href.split('?')[1]);
}

export function getQueryPath(path = '', query = {}): string {
  const search = stringify(query);
  if (search.length) {
    return `${path}?${search}`;
  }
  return path;
}

// eslint-disable-next-line max-len
const reg = /(((^https?:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+(?::\d+)?|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)$/;
export function isUrl(path: string): boolean {
  return reg.test(path);
}

export function isCompleteTripRecord(record): boolean {
  return (
    record &&
    'attachments' in record &&
    'start_airport' in record &&
    'end_airport' in record &&
    'callsign' in record &&
    'status' in record &&
    'flights' in record &&
    'captain' in record &&
    'first_officer' in record &&
    'versions' in record
  );
}

export function isCompleteFlightRecord(record, includeTripRecordCheck = true): boolean {
  return (
    record &&
    'apu_seconds' in record &&
    'arrival_airport_details' in record &&
    'arrival_airport_id' in record &&
    'arrival_fuel' in record &&
    'attachments' in record &&
    'defects' in record &&
    'deice_events' in record &&
    'departure_airport_details' in record &&
    'departure_airport_id' in record &&
    'departure_fuel_adjustment' in record &&
    'departure_fuel_log' in record &&
    'departure_fuel_pre_uplifts' in record &&
    'fuel_uplifts' in record &&
    'hobbs_arrival' in record &&
    'hobbs_departure' in record &&
    'oil_uplift' in record &&
    'pilot_flying' in record &&
    'pilot_monitoring' in record &&
    'postflight_signature_image_url' in record &&
    'postflight_signature_url' in record &&
    'preflight_signature_image_url' in record &&
    'preflight_signature_url' in record &&
    'prior_corrosion_risk_details' in record &&
    'prior_corrosion_risk_score' in record &&
    'remote_deice' in record &&
    'trip' in record &&
    'versions' in record &&
    'wet_weather_percentage' in record &&
    (!includeTripRecordCheck || isCompleteTripRecord(record.trip))
  );
}
